/* Content settings shared by the custom Torque templates. */
(() => {
  'use strict';
  if (window.TorqueContent) return;
  const controllers = new WeakMap();
  const mobile = window.matchMedia('(max-width: 720px)');
  const read = (root, attribute) => {
    const scope = root.closest('.shopify-section') || root;
    try { return JSON.parse(scope.querySelector(`[${attribute}]`)?.textContent || '{}'); }
    catch { return {}; }
  };
  const labels = root => {
    const values = read(root, 'data-torque-editor-labels');
    return (key, fallback = '') => {
      const value = values[key];
      return (mobile.matches && value?.mobile?.trim()) || value?.text?.trim() || fallback;
    };
  };
  const imageListeners = new WeakSet();
  const image = (node, source, alt) => {
    if (!node || !source) return;
    node.dataset.tqOriginalSrc = source;
    if (node.hasAttribute('data-tq-responsive-image')) {
      const url = new URL(source, document.baseURI);
      const shopifyImage = /\.(?:jpe?g|png|webp|avif)$/i.test(url.pathname)
        && (url.pathname.startsWith('/cdn/shop/') || /(^|\.)cdn\.shopify\.com$/.test(url.hostname));
      if (shopifyImage) {
        const maximum = Math.min(3200, Number(node.dataset.tqImageWidth) || 1600);
        const widths = [...new Set([96, 160, 240, 320, 390, 570, 780, 1080, 1600, 2200, 3000, maximum])]
          .filter(width => width <= maximum).sort((left, right) => left - right);
        node.srcset = widths.map(width => {
          const candidate = new URL(url);
          candidate.searchParams.set('width', String(width));
          return `${candidate.href} ${width}w`;
        }).join(', ');
        node.sizes = node.dataset.tqImageSizes || node.sizes || '100vw';
      } else {
        node.removeAttribute('srcset');
      }
      if (!imageListeners.has(node)) {
        node.addEventListener('load', () => {
          if (node.naturalWidth && node.naturalHeight) {
            node.width = node.naturalWidth;
            node.height = node.naturalHeight;
          }
        });
        imageListeners.add(node);
      }
    }
    if (node.getAttribute('src') !== source) node.setAttribute('src', source);
    if (alt !== undefined) node.setAttribute('alt', alt);
  };
  window.TorqueContent = { read, labels, image };

  function init(root) {
    if (controllers.has(root)) return;
    const pictures = [...root.querySelectorAll('img[data-tq-focal]')];
    const docks = [...root.querySelectorAll('#mcta,[data-pdp-sticky]')];
    const reviewObservers = [];
    const reviewCleanups = [];
    // The review vendor inserts these photographs after Liquid has rendered.
    // Supply only missing intrinsic dimensions after the real image loads;
    // its frame, crop, source, and existing vendor dimensions stay untouched.
    root.querySelectorAll('.jdgm-review-widget').forEach(widget => {
      const watched = new WeakSet();
      const reserveImages = () => widget.querySelectorAll('.jm-cluster > .jm-box > .jm-frame > img').forEach(photo => {
        if (watched.has(photo) || (Number(photo.getAttribute('width')) > 0 && Number(photo.getAttribute('height')) > 0)) return;
        watched.add(photo);
        const dimensions = () => {
          if (photo.naturalWidth && photo.naturalHeight && !(Number(photo.getAttribute('width')) > 0 && Number(photo.getAttribute('height')) > 0)) {
            photo.width = photo.naturalWidth;
            photo.height = photo.naturalHeight;
          }
        };
        if (photo.complete) dimensions();
        photo.addEventListener('load', dimensions);
        reviewCleanups.push(() => photo.removeEventListener('load', dimensions));
      });
      const reviewObserver = new MutationObserver(reserveImages);
      reviewObserver.observe(widget, { childList: true, subtree: true });
      reviewObservers.push(reviewObserver);
      reserveImages();
    });
    let frame = 0;
    const update = () => {
      const height = mobile.matches ? Math.max(0, ...docks.map(dock => dock.getBoundingClientRect().height)) : 0;
      pictures.forEach(img => {
        const position = mobile.matches ? img.dataset.tqFocalMobile : img.dataset.tqFocal;
        if (img.style.objectPosition !== position) img.style.objectPosition = position;
      });
      // The dock sits under the header; reserve 61px and only grow scroll clearance when edited copy expands it.
      const dockSpace = `${Math.max(0, height - 61)}px`;
      const clearance = `${Math.max(61, height + 12)}px`;
      if (root.style.getPropertyValue('--tq-editor-dock-space') !== dockSpace) root.style.setProperty('--tq-editor-dock-space', dockSpace);
      if (document.documentElement.style.getPropertyValue('--tq-editor-scroll-clearance') !== clearance) document.documentElement.style.setProperty('--tq-editor-scroll-clearance', clearance);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    docks.forEach(dock => observer.observe(dock));
    mobile.addEventListener('change', schedule);
    update();
    controllers.set(root, () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      reviewObservers.forEach(reviewObserver => reviewObserver.disconnect());
      reviewCleanups.forEach(cleanup => cleanup());
      mobile.removeEventListener('change', schedule);
      controllers.delete(root);
    });
  }
  const selector = '.torque-homepage-v6,.tq-activity';
  const roots = scope => [scope, ...(scope.querySelectorAll?.(selector) || [])].filter(node => node.matches?.(selector));
  const boot = scope => roots(scope).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => roots(event.target).forEach(root => controllers.get(root)?.()));
  document.addEventListener('shopify:block:select', event => {
    const block = event.target;
    block.closest('.rv')?.classList.add('in');
    block.classList.add('in');
    // Selecting an area reveals its content without opening its first FAQ.
    const area = block.hasAttribute('data-tq-content-area');
    if (area) block.querySelectorAll('.rv').forEach(node => node.classList.add('in'));
    const faqGroup = block.closest('.faq-disclosure') || (area && block.querySelector('.faq-disclosure'));
    if (faqGroup) faqGroup.open = true;
    const disclosure = block.matches('details') ? block : !area && block.querySelector('details');
    if (disclosure) disclosure.open = true;
    block.hidden = false;
    block.scrollIntoView({ block: 'nearest', behavior: 'instant' });
  });
  document.addEventListener('shopify:block:deselect', event => {
    if (event.target.hasAttribute('data-tq-editor-hidden')) event.target.hidden = true;
  });
})();
